import { Box, SearchField, matchText } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { useMutateDefaultShifts } from 'modules/shift_management/hooks/useMutateDefaultShifts'
import { useSelectionState } from 'modules/shift_management/hooks/useSelection'
import DefaultShiftWithParamsForm from 'modules/shift_management/lib/forms/DefaultShiftWithParamsForm'
import { DefaultShift } from 'modules/shift_management/lib/types'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import QuickActionsItem from './QuickActionsItem'

interface Props {
  savedShifts: DefaultShift[]
  onClose: () => void
}

const Content = ({ savedShifts, onClose }: Props) => {
  const { daysByRow } = useSelectionState()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { onSubmit } = useMutateDefaultShifts({
    daysByRow,
    weekDays: '',
    from: 'quick_actions',
    withSearch: search,
    advancedOptions: false,
  })

  const filteredVisibleDefaultShifts = useUnsafeMemo(
    () => savedShifts.filter((ds) => matchText(debouncedSearch, ds.title)),
    [debouncedSearch, savedShifts]
  )

  const defaultShiftIds = savedShifts.map((ds) => ds.id)

  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })

  const handleSubmit = (defaultShift: DefaultShift) => {
    const newForm = new DefaultShiftWithParamsForm({
      id: defaultShift.id,
      title: defaultShift.title,
      start_at: defaultShift.startAt,
      end_at: defaultShift.endAt,
      extra_hours: defaultShift.extraHours,
      color: defaultShift.color,
      overnight_shift: defaultShift.overnightShift,
      location_id: defaultShift.locationId,
      work_area_id: defaultShift.workAreaId,
      notes: defaultShift.notes,
    })
    onSubmit([newForm])
  }

  return (
    <Box gap="s16">
      <Box>
        <SearchField
          placeholder={i18n.t('shift_management.add_shift.search_by_title')}
          onSearch={onSearch}
          search={search}
        />
      </Box>
      <Box gap="s8" maxHeight="s240" overflowY="auto">
        {filteredVisibleDefaultShifts.map((defaultShift: DefaultShift) => (
          <PopoverMenu.Close key={defaultShift.id}>
            <Box onClick={onClose}>
              <QuickActionsItem
                onSubmit={() => handleSubmit(defaultShift)}
                defaultShift={defaultShift}
                plannedBreaks={plannedBreaksCollection.active.filterByDefaultShift(defaultShift.id)}
              />
            </Box>
          </PopoverMenu.Close>
        ))}
      </Box>
    </Box>
  )
}

export default observer(Content)
